import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFees } from '../../context/FeeContext';
import BookingCardExtraCharges from './BookingCardExtraCharges';
import AutoReleaseCountdown from '../completion/AutoReleaseCountdown';
import PinMap from './PinMap';
import LiveTrackingMap from './LiveTrackingMap';
import AddressText from './AddressText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingsListView = ({
  bookings,
  bookingFilter,
  setBookingFilter,
  effectiveRole,
  onViewDetails,
  onAcceptBooking,
  onRejectBooking,
  onCancelBooking,
  onMessageProvider,
  onDeleteBooking,
  onHideBooking,
  onRefresh,
  formatBookingDate,
  formatBookingTime,
  getStatusColor
}) => {
  const { t } = useTranslation();
  const { feeRates, calculateClientTotal, calculateProviderPayout, getBookingPricing } = useFees();
  const [onMyWayBusy, setOnMyWayBusy] = useState(null);

  const handleOnMyWay = async (bookingId) => {
    setOnMyWayBusy(bookingId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bookings/${bookingId}/on-my-way`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (onRefresh) await onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || t('bookingsList.onMyWayError'));
    } finally {
      setOnMyWayBusy(null);
    }
  };

  // Per-booking totals of client-approved (but unpaid) extra charges, reported up
  // by each card so the displayed amounts reflect the extras on both sides.
  const [extraTotals, setExtraTotals] = useState({});
  const handleApprovedTotal = useCallback((id, totals) => {
    setExtraTotals((prev) => {
      const cur = prev[id];
      if (cur && cur.clientTotal === totals.clientTotal && cur.providerPayout === totals.providerPayout) return prev;
      return { ...prev, [id]: totals };
    });
  }, []);
  
  const filteredBookings = bookings.filter(booking => {
    if (bookingFilter === 'all') return true;
    return booking.status === bookingFilter;
  });

  return (
    <>
      {/* Filter Tabs for Bookings */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {['all', 'pending', 'accepted', 'in_progress', 'completion_requested', 'disputed', 'completed', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setBookingFilter(status)}
            className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition ${
              bookingFilter === status
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`filter-${status}`}
          >
            {status === 'all' ? t('bookingsList.all') : t('bookingsList.statuses.' + status, status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}
            {status !== 'all' && (
              <span className="ml-2 text-xs opacity-70">
                ({bookings.filter(b => b.status === status).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Bookings List */}
      {filteredBookings.length > 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {filteredBookings.map((booking) => (
              <div
                key={booking.id}
                className="p-6 hover:bg-gray-50 transition"
                data-testid={`booking-item-${booking.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-bold text-gray-900 text-lg">{booking.service_title}</h3>
                      {/* Booking Order Code for service bookings */}
                      {booking.booking_order_code && (
                        <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs px-2 py-0.5 rounded-full font-medium" title="Service Booking Code">
                          #SE-{booking.booking_order_code}
                        </span>
                      )}
                      {/* Job Order Code for job-based bookings */}
                      {booking.type === 'job' && booking.job_order_code && (
                        <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs px-2 py-0.5 rounded-full font-medium" title="Job Order Code">
                          #JO-{booking.job_order_code}
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(booking.status)}`}>
                        {booking.status?.replace(/_/g, ' ')}
                      </span>
                      {booking.was_disputed && booking.status === 'completed' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200" data-testid={`was-disputed-tag-${booking.id}`}>
                          {t('bookingsList.wasDisputed')}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-600 mb-3">
                      {effectiveRole === 'provider' ? (
                        `${t('bookingsList.client')}: ${booking.client_name}`
                      ) : (
                        <>
                          {t('bookingsList.provider')}:{' '}
                          <button
                            type="button"
                            onClick={() => onMessageProvider && onMessageProvider(booking)}
                            className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2"
                            title={t('bookingsList.messageProvider', 'Message provider')}
                            data-testid={`message-provider-${booking.id}`}
                          >
                            {booking.provider_name}
                          </button>
                        </>
                      )}
                    </p>

                    {/* Price-adjustment notice (client): the provider changed the agreed
                        price, so warn the client and let them cancel if they didn't agree. */}
                    {effectiveRole === 'client' && booking.price_adjusted && booking.status === 'accepted' && booking.payment_status !== 'paid' && (
                      <div
                        className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2"
                        data-testid={`price-adjusted-notice-${booking.id}`}
                      >
                        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                        <span>
                          {t('bookingsList.priceAdjusted', 'Price changed by provider')}: ₱{Number(booking.original_price || 0).toLocaleString()} → <strong>₱{Number(booking.final_amount || 0).toLocaleString()}</strong>. {t('bookingsList.priceAdjustedHint', "Cancel if you didn't agree to this.")}
                        </span>
                      </div>
                    )}

                    {/* Location line — role aware:
                        • Client sees the provider's registered area (barangay level).
                        • Provider sees the client's full service address. */}
                    {effectiveRole === 'client' ? (
                      booking.provider_area && (
                        <p className="text-sm text-gray-500 mb-3 flex items-start gap-1">
                          <svg className="w-4 h-4 mt-0.5 shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span data-testid={`booking-location-${booking.id}`}>{t('bookingsList.providerArea')}: {booking.provider_area}</span>
                        </p>
                      )
                    ) : (
                      (booking.location || booking.service_lat != null) && (
                        <p className="text-sm text-gray-500 mb-3 flex items-start gap-1">
                          <svg className="w-4 h-4 mt-0.5 shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <AddressText
                            lat={booking.service_lat}
                            lng={booking.service_lng}
                            fallback={booking.location}
                            testId={`booking-location-${booking.id}`}
                          />
                        </p>
                      )
                    )}
                    
                    {/* Date and Time */}
                    {(booking.scheduled_date || booking.scheduled_time) && (
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        {booking.scheduled_date && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {formatBookingDate(booking.scheduled_date)}
                          </span>
                        )}
                        {booking.scheduled_time && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {formatBookingTime(booking.scheduled_time)}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Pricing - Show different amounts based on role */}
                    {(booking.final_amount || booking.price) && (
                      <div className="text-sm">
                        {effectiveRole === 'provider' ? (
                          <>
                            <span className="text-gray-600">{t('bookingsList.yourEarnings')}</span>
                            <span className="font-semibold text-green-600 text-lg">
                              ₱{(getBookingPricing(booking).providerPayout + (extraTotals[booking.id]?.providerPayout || 0)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </span>
                            <span className="text-xs text-gray-500 ml-2">
                              {extraTotals[booking.id]?.providerPayout
                                ? t('bookingsList.afterFeeExtra', { rate: getBookingPricing(booking).providerFeeRate, extra: Number(extraTotals[booking.id].providerPayout).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })
                                : t('bookingsList.afterFee', { rate: getBookingPricing(booking).providerFeeRate })}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-gray-600">{t('bookingsList.total')}</span>
                            <span className="font-semibold text-gray-900 text-lg">
                              ₱{(getBookingPricing(booking).clientTotal + (extraTotals[booking.id]?.clientTotal || 0)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </span>
                            <span className="text-xs text-gray-500 ml-2">
                              {extraTotals[booking.id]?.clientTotal
                                ? t('bookingsList.inclFeeExtra', { extra: Number(extraTotals[booking.id].clientTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })
                                : t('bookingsList.inclFee')}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-row flex-wrap items-center sm:flex-col sm:items-end gap-2 sm:ml-4 shrink-0">
                    <button
                      onClick={() => onViewDetails(booking)}
                      className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                      data-testid={`view-details-${booking.id}`}
                    >
                      {t('bookingsList.viewDetails')}
                    </button>
                    
                    {booking.status === 'pending' && effectiveRole === 'provider' && (
                      <button
                        onClick={() => onAcceptBooking(booking)}
                        className="text-green-600 hover:text-green-700 font-medium text-sm"
                        data-testid={`accept-booking-${booking.id}`}
                      >
                        {t('bookingsList.acceptBooking')}
                      </button>
                    )}

                    {booking.status === 'pending' && effectiveRole === 'provider' && (
                      <button
                        onClick={() => onRejectBooking && onRejectBooking(booking)}
                        className="text-red-600 hover:text-red-700 font-medium text-sm"
                        data-testid={`reject-booking-${booking.id}`}
                      >
                        {t('bookingsList.rejectBooking', 'Reject Booking')}
                      </button>
                    )}

                    {booking.status === 'pending' && effectiveRole === 'client' && (
                      <button
                        onClick={() => onCancelBooking(booking)}
                        className="text-red-600 hover:text-red-700 font-medium text-sm"
                        data-testid={`cancel-booking-${booking.id}`}
                      >
                        {t('bookingsList.cancel')}
                      </button>
                    )}

                    {/* Client can cancel a price-adjusted, accepted & unpaid booking. */}
                    {booking.status === 'accepted' && effectiveRole === 'client' && booking.price_adjusted && booking.payment_status !== 'paid' && (
                      <button
                        onClick={() => onCancelBooking(booking)}
                        className="text-red-600 hover:text-red-700 font-medium text-sm"
                        data-testid={`cancel-adjusted-booking-${booking.id}`}
                      >
                        {t('bookingsList.cancel')}
                      </button>
                    )}

                    {booking.status === 'completion_requested' && (
                      <button
                        onClick={() => onViewDetails(booking)}
                        className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-purple-700 transition"
                        data-testid={`review-completion-${booking.id}`}
                      >
                        {t('bookingsList.reviewCompletion')}
                      </button>
                    )}

                    {/* Provider: stage-aware tracking action for accepted/in_progress bookings.
                        "On My Way" is surfaced directly on the card so it's discoverable
                        without opening the completion flow. */}
                    {['accepted', 'in_progress'].includes(booking.status) && effectiveRole === 'provider' && (
                      !booking.on_my_way_at ? (
                        <button
                          onClick={() => handleOnMyWay(booking.id)}
                          disabled={onMyWayBusy === booking.id}
                          className="bg-sky-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-sky-700 transition disabled:opacity-50"
                          data-testid={`on-my-way-card-${booking.id}`}
                        >
                          {onMyWayBusy === booking.id ? t('bookingsList.updating') : t('bookingsList.onMyWay')}
                        </button>
                      ) : !booking.job_ended_at ? (
                        <button
                          onClick={() => onViewDetails(booking)}
                          className="bg-sky-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-sky-700 transition"
                          data-testid={`manage-job-${booking.id}`}
                        >
                          {t('bookingsList.manageJob')}
                        </button>
                      ) : (
                        <button
                          onClick={() => onViewDetails(booking)}
                          className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-orange-600 transition"
                          data-testid={`request-completion-${booking.id}`}
                        >
                          {t('bookingsList.requestCompletion')}
                        </button>
                      )
                    )}

                    {booking.status === 'accepted' && effectiveRole === 'client' && booking.payment_status !== 'paid' && (
                      <Link
                        to={`/payment/${booking.id}`}
                        className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-green-600"
                        data-testid={`pay-now-${booking.id}`}
                      >
                        {booking.payment_status ? t('bookingsList.retryPayment') : t('bookingsList.payNow')}
                      </Link>
                    )}

                    {/* Delete/Hide button for cancelled/rejected bookings */}
                    {['cancelled', 'rejected'].includes(booking.status) && (
                      <button
                        onClick={() => effectiveRole === 'provider' 
                          ? onHideBooking(booking.id) 
                          : onDeleteBooking(booking.id)
                        }
                        className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center gap-1"
                        data-testid={`delete-booking-${booking.id}`}
                      >
                        {effectiveRole === 'provider' ? t('bookingsList.remove') : t('bookingsList.delete')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Location & live tracking map (active bookings only).
                    Live navigation runs until the provider STARTS the service, then the
                    static pin/map is hidden (service is in progress or finished). */}
                {['accepted', 'in_progress'].includes(booking.status) && !booking.job_started_at && (
                  booking.on_my_way_at ? (
                    <div className="mt-4 bg-sky-50 rounded-xl p-3 border border-sky-100" data-testid={`card-live-map-${booking.id}`}>
                      <p className="text-xs font-semibold text-sky-800 mb-2 flex items-center gap-1">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-600"></span>
                        </span>
                        {effectiveRole === 'provider' ? t('bookingsList.liveNavigation', 'Live navigation to client') : t('bookingsList.liveTracking', 'Provider on the way — live tracking')}
                      </p>
                      <LiveTrackingMap
                        booking={booking}
                        effectiveRole={effectiveRole}
                        height="13rem"
                        shareLocation={effectiveRole === 'provider'}
                        testId={`card-tracking-${booking.id}`}
                      />
                    </div>
                  ) : booking.service_lat != null ? (
                    <div className="mt-4" data-testid={`card-pin-map-${booking.id}`}>
                      <PinMap lat={booking.service_lat} lng={booking.service_lng} height="11rem" testId={`card-pin-${booking.id}`} />
                    </div>
                  ) : null
                )}

                {/* Auto-release countdown: visible to both parties once a completion
                    request is pending, so they know when funds auto-release (24h). */}
                {booking.status === 'completion_requested' && booking.completion_requested_at && (
                  <div className="mt-3">
                    <AutoReleaseCountdown
                      requestedAt={booking.completion_requested_at}
                      variant={effectiveRole === 'provider' ? 'provider' : 'client'}
                      hours={feeRates.auto_confirm_hours}
                    />
                  </div>
                )}

                {/* Card-level extra-charge approval (client, accepted+unpaid) so it's visible without opening details */}
                <BookingCardExtraCharges booking={booking} effectiveRole={effectiveRole} onApprovedTotal={handleApprovedTotal} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {bookingFilter === 'all' ? t('bookingsList.noBookings') : t('bookingsList.noFilteredBookings', { filter: bookingFilter.replace(/_/g, ' ') })}
          </h3>
          <p className="text-gray-500 mb-6">
            {effectiveRole === 'client' 
              ? t('bookingsList.clientEmpty')
              : t('bookingsList.providerEmpty')}
          </p>
          {effectiveRole === 'client' && (
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {t('bookingsList.browseServices')}
            </Link>
          )}
        </div>
      )}
    </>
  );
};

export default BookingsListView;
